import { useCallback, useEffect, useState } from 'react';

const EDGE_TOLERANCE = 2;

/**
 * Tracks whether a horizontally scrollable strip has content hidden before or
 * after its visible edge, so callers can render visible continuation controls
 * instead of relying on an invisible scrollbar or a guessed swipe.
 */
const useScrollOverflow = (ref, { axis = 'x', enabled = true } = {}) => {
  const [overflow, setOverflow] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element || !enabled) {
      setOverflow((current) => (current.start || current.end ? { start: false, end: false } : current));
      return;
    }

    const horizontal = axis === 'x';
    const position = horizontal ? element.scrollLeft : element.scrollTop;
    const visible = horizontal ? element.clientWidth : element.clientHeight;
    const total = horizontal ? element.scrollWidth : element.scrollHeight;
    const next = {
      start: position > EDGE_TOLERANCE,
      end: position + visible < total - EDGE_TOLERANCE,
    };

    setOverflow((current) => (
      current.start === next.start && current.end === next.end ? current : next
    ));
  }, [axis, enabled, ref]);

  useEffect(() => {
    const element = ref.current;
    measure();
    if (!element || !enabled) return undefined;

    element.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const created = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    // Tolerate partial ResizeObserver stand-ins (test environments).
    const observer = typeof created?.observe === 'function' ? created : null;
    observer?.observe(element);
    Array.from(element.children).forEach((child) => observer?.observe(child));

    return () => {
      element.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect?.();
    };
  }, [enabled, measure, ref]);

  const scrollByPage = useCallback((direction) => {
    const element = ref.current;
    if (!element) return;
    const horizontal = axis === 'x';
    const distance = Math.max((horizontal ? element.clientWidth : element.clientHeight) * 0.75, 80);
    const delta = direction === 'start' ? -distance : distance;
    if (typeof element.scrollBy === 'function') {
      element.scrollBy(horizontal ? { left: delta, behavior: 'smooth' } : { top: delta, behavior: 'smooth' });
    } else if (horizontal) {
      element.scrollLeft += delta;
    } else {
      element.scrollTop += delta;
    }
  }, [axis, ref]);

  return { ...overflow, measure, scrollByPage };
};

/**
 * Scroll a strip (not the document) so the selected item is fully visible.
 * Element.scrollIntoView would also move the page vertically, which is
 * exactly the jump a persistent tab row must not cause.
 */
export const revealWithinStrip = (strip, item, axis = 'x') => {
  if (!strip || !item) return;

  const horizontal = axis === 'x';
  const stripBox = strip.getBoundingClientRect();
  const itemBox = item.getBoundingClientRect();
  const stripStart = horizontal ? stripBox.left : stripBox.top;
  const stripEnd = horizontal ? stripBox.right : stripBox.bottom;
  const itemStart = horizontal ? itemBox.left : itemBox.top;
  const itemEnd = horizontal ? itemBox.right : itemBox.bottom;
  // Leave room for the continuation controls that overlay the strip edges.
  const inset = 36;
  let delta = 0;

  if (itemStart < stripStart + inset) {
    delta = itemStart - stripStart - inset;
  } else if (itemEnd > stripEnd - inset) {
    delta = itemEnd - stripEnd + inset;
  }

  if (delta === 0) return;
  if (horizontal) {
    strip.scrollLeft += delta;
  } else {
    strip.scrollTop += delta;
  }
};

export default useScrollOverflow;
